import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import {
  equal,
  filterFlightsByCondition,
  filterTravelersByCondition,
  isEmptyObject,
  isStringEmpty,
  notEqual,
  parseEmptyArray,
} from '@common/helpers';
import { FLIGHT_STATUS_CODE } from '@common/helpers/constants/my-booking/my_booking_service.contant';
import {
  MListData,
  PREVIOUS_PAGE_IDENTIFIER,
  PassengerType,
  ServicingFunctionIdType,
  ServicingPageIdType,
} from '@common/interfaces';
import { AirportI18n } from '@common/interfaces/master/airport_i18n_all';
import { MFucntionIneligibleReason } from '@common/interfaces/master/m_function_ineligble_reason';
import { MPreOrderMealFlight } from '@common/interfaces/master/m_preorder_meal_flight';
import { MSpecialMeal } from '@common/interfaces/master/m_special_meal';
import { ChargeableLounge, FirstBaggage, MealInformation } from '@common/interfaces/mybooking/mybooking.interface';
import {
  CallCenterRequestBaggage,
  FirstBaggagePassengerMap,
  FirstBaggagePnr,
  FirstBaggageSegmentMap,
} from '@common/interfaces/mybooking/order-baggage.interface';
import {
  AirportService,
  AppliedMealList,
  FlightMealInfo,
  Lounge,
  Meal,
  PassengerLoungeMap,
  TravelerMealInfo,
} from '@common/interfaces/mybooking/order-services.interface';
import { SeatReservationStatus } from '@common/interfaces/servicing-seatmap/seat-reservation-status';
import { LOAD_MASTERS } from '@common/master';
import { ServicingCommonStoreService } from '@common/services';
import { MyBookingServiceService } from '@common/services/my-booking/my-booking-service/my-booking-service.service';
import { MyBookingService } from '@common/services/my-booking/my-booking.service';
import { MasterJsonKeyPrefix } from '@conf/asw-master.config';
import { SupportComponent } from '@lib/components/support-class';
import { DialogClickType, LogType, SessionStorageName } from '@lib/interfaces';
import {
  AswMasterService,
  CommonLibService,
  DialogDisplayService,
  LoggerDatadogService,
  PageInitService,
  PageLoadingService,
} from '@lib/services';
import { Subscription, forkJoin, map, of, take } from 'rxjs';
import {
  Bound,
  GetMealResponseData,
  GetOrderResponse,
  GetOrderResponseData,
  GetOrderResponseDataOrderType,
  Traveler,
  Type1,
} from 'src/sdk-servicing';
import { AppConstants } from '@conf/app.constants';

const MY_BOOKING_SERVICES_MASTER_DATA_LIST = [
  LOAD_MASTERS.LISTDATA_ALL,
  LOAD_MASTERS.AIRPORT_I18N_ALL,
  LOAD_MASTERS.FUNCTION_INELIGIBLE_REASON_PK,
];

type IMyBookingServiceMasterData = [MListData[], AirportI18n, MFucntionIneligibleReason];
@Component({
  selector: 'asw-my-booking-services',
  templateUrl: './my-booking-services.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyBookingServicesComponent extends SupportComponent {
  private _subscriptions: Subscription = new Subscription();
  /** 予約詳細(サービス) */
  pageId: string = ServicingPageIdType.MYBOOKING_SERVICE;
  /** 予約確認 */
  functionId: string = ServicingFunctionIdType.BOOKING_CONFIRMATION;
  pnr!: GetOrderResponseData;
  // 当該搭乗者情報
  travelers: Traveler[] = [];
  firstBaggage!: FirstBaggage;
  mealInformation!: MealInformation;
  chargeableLounge!: ChargeableLounge;
  seatReservationStatus!: SeatReservationStatus;

  // 定数用
  public appConstants = AppConstants;

  constructor(
    private readonly _common: CommonLibService,
    private readonly _change: ChangeDetectorRef,
    private readonly _pageInitService: PageInitService,
    private readonly _myBookingServiceService: MyBookingServiceService,
    private readonly _aswMasterService: AswMasterService,
    private readonly _servicingCommonStoreService: ServicingCommonStoreService,
    private readonly _loggerDatadogService: LoggerDatadogService,
    private readonly _myBookingService: MyBookingService,
    private readonly _pageLoadingService: PageLoadingService,
    private _dialogService: DialogDisplayService
  ) {
    /** (1) 1.	画面の初期化処理(G01-003)の初期化開始処理を呼び出す。 */
    super(_common);
  }

  /** (1) 1.	画面の初期化処理(G01-003)の初期化開始処理を呼び出す。*/
  screenInit(): void {
    const identificationId = sessionStorage.getItem(SessionStorageName.IDENTIFICATION_ID);
    this._loggerDatadogService.info(LogType.PAGE_VIEW, {
      identificationId,
      functionId_pageId: `${this.functionId}_${this.pageId}`,
    });
  }

  onInit(): void {
    this._pageLoadingService.startLoading(true);
    this._subscriptions.add(
      forkJoin([
        this._aswMasterService.load(MY_BOOKING_SERVICES_MASTER_DATA_LIST, true).pipe(
          map((res) => res as IMyBookingServiceMasterData),
          take(1)
        ),
        this._servicingCommonStoreService.getServicingCommonModel$().pipe(take(1)),
      ]).subscribe((res) => {
        const [[mListData, airportI18nAll, mFucntionIneligibleReason], servicingCommon] = res;
        this._myBookingServiceService.myBookingServiceMasterData$.next({
          listData: parseEmptyArray(mListData),
          airportI18nAll: airportI18nAll,
          mFucntionIneligibleReason,
        });

        this.pnr = servicingCommon.getOrder ?? ({} as GetOrderResponseData);
        this.travelers = parseEmptyArray(this.pnr?.travelers);
        this.firstBaggage = servicingCommon.firstBaggage ?? ({} as FirstBaggage);
        this.mealInformation = servicingCommon.meal ?? ({} as MealInformation);
        this.chargeableLounge = servicingCommon.chargeableLounge ?? ({} as ChargeableLounge);
        this.seatReservationStatus = servicingCommon.seatReservationStatus ?? ({} as SeatReservationStatus);
        this._myBookingServiceService.myBookingServiceInformation$.next(servicingCommon);

        this.screenInit();

        // 2.	画面情報に以下の内容を設定する。
        this._common.aswCommonStoreService.updateAswCommon({
          functionId: this.functionId,
          pageId: this.pageId,
          subFunctionId: '',
          subPageId: '',
          isEnabledLogin: false,
          isUpgrade: false,
        });

        this.initGlobalVariable();

        this._change.markForCheck();

        const completedFunction = this._servicingCommonStoreService.servicingCommon?.completedFunction ?? '';

        this._pageInitService.endInit(
          of({
            getOrderReply: { data: this.pnr },
            aswContext: this._common.aswContextStoreService.aswContextData,
            completedFunction,
          })
        );
        this._pageLoadingService.endLoading(true);
      })
    );
  }

  /** 初期表示処理 */
  init(): void {
    this._subscriptions.add(
      this._myBookingService.onInitFinishedSubject.subscribe((initFinished) => {
        if (initFinished) {
          this.onInit();
        }
      })
    );
  }

  /** 3. 画面項目表示用、以下の処理を行う。 */
  initGlobalVariable = (): void => {
    const flights =
      this.pnr?.air?.bounds?.flatMap((bound: Bound) => bound.flights ?? ([] as Type1[])) ?? ([] as Type1[]);
    const flightsByUN = filterFlightsByCondition(this.pnr, [
      { key: 'statusCode', value: FLIGHT_STATUS_CODE.UN, operator: notEqual },
      { key: 'isStandbyCheckInHL', value: false, operator: equal },
    ]);
    const travelersByINF = filterTravelersByCondition(this.pnr, [
      {
        key: 'passengerTypeCode',
        value: PassengerType.INF,
        operator: notEqual,
      },
    ]);

    // 当該セグメント情報(事前座席指定セグメント情報リスト用)
    // all: SERVICING-MYBOOKING-018
    let flightsByUNForSeatFlightInforList: Type1[] = [];
    if (
      this.pnr.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Nh ||
      this.pnr.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Nh3
    ) {
      // NH0 PNR / NH3 PNRの場合、自社マケ自社オペ便のみ可能とする。
      flightsByUNForSeatFlightInforList = filterFlightsByCondition(this.pnr, [
        { key: 'statusCode', value: FLIGHT_STATUS_CODE.UN, operator: notEqual },
        { key: 'marketingAirlineCode', value: this.appConstants.CARRIER.TWO_LETTER, operator: equal },
        { key: 'operatingAirlineCode', value: this.appConstants.CARRIER.TWO_LETTER, operator: equal },
        { key: 'isStandbyCheckInHL', value: false, operator: equal },
      ]);
    } else {
      // NH0 PNR / NH3 PNR以外の場合
      flightsByUNForSeatFlightInforList = filterFlightsByCondition(this.pnr, [
        { key: 'statusCode', value: FLIGHT_STATUS_CODE.UN, operator: notEqual },
        { key: 'isStandbyCheckInHL', value: false, operator: equal },
      ]);
    }

    // 当該セグメント情報(ジュニアパイロットセグメント情報リスト用)
    // all: SERVICING-service-024
    let flightsByUNForJuniorPilotFlightInforList: Type1[] = [];
    if (
      this.pnr.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Nh ||
      this.pnr.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Nh3
    ) {
      // NH0 PNR / NH3 PNRの場合、
      let flights: Type1[] = [];
      this.pnr?.air?.bounds?.forEach((bound) => {
        // ・1バウンドのフライトが自社マケ自社オペ便のみで構成されている場合(condition1)
        let condition1 = (bound.flights ?? ([] as Type1[])).every(
          (flight: Type1) =>
            flight.marketingAirlineCode === this.appConstants.CARRIER.TWO_LETTER &&
            flight.operatingAirlineCode === this.appConstants.CARRIER.TWO_LETTER
        );

        // ・1バウンドのフライトの1セグ目が自社マケ自社オペ便である場合(condition2)
        // ・かつ2セグ目以降がNHマケ自社オペ便で構成されてる場合(condition3)
        let condition2 = (bound.flights ?? ([] as Type1[]))
          .slice(0, 1)
          .every(
            (flight: Type1) =>
              flight.marketingAirlineCode === this.appConstants.CARRIER.TWO_LETTER &&
              flight.operatingAirlineCode === this.appConstants.CARRIER.TWO_LETTER
          );
        let condition3 = (bound.flights ?? ([] as Type1[]))
          .slice(1)
          .every(
            (flight: Type1) =>
              flight.marketingAirlineCode === 'NH' &&
              flight.operatingAirlineCode === this.appConstants.CARRIER.TWO_LETTER
          );

        if (condition1 || (condition2 && condition3)) {
          (bound.flights ?? ([] as Type1[])).forEach((flight: Type1) => {
            if (flight.statusCode !== FLIGHT_STATUS_CODE.UN && flight?.isStandbyCheckInHL !== true) {
              flights.push(flight);
            }
          });
        }
      });
      flightsByUNForJuniorPilotFlightInforList = flights;
    } else {
      // NH0 PNR / NH3 PNR以外の場合
      flightsByUNForJuniorPilotFlightInforList = filterFlightsByCondition(this.pnr, [
        { key: 'statusCode', value: FLIGHT_STATUS_CODE.UN, operator: notEqual },
        { key: 'isStandbyCheckInHL', value: false, operator: equal },
      ]);
    }

    /** 3.1. 事前座席指定情報に設定する値を作成する。 */
    /** 3.1.3. 当該セグメント情報.statusCode≠”UN”、かつ当該セグメント情報.isStandbyCheckInHL=falseの場合、事前座席指定セグメント情報リストに当該セグメント情報を追加する。 */
    const seatFlightInforList = flightsByUNForSeatFlightInforList;
    /** 3.1.4. 当該搭乗者情報.passengerTypeCode≠”INF”(席なし幼児)の場合、事前座席指定搭乗者情報リストに当該搭乗者情報を追加する。 */
    const seatPassengerInforList = travelersByINF;

    /** 3.2. 事前追加手荷物情報に設定する値を作成する。 */
    const firstBaggageBoundInforList = this.getFirstBaggageBoundInforList();
    /** 3.2.4. 事前追加手荷物搭乗者情報リストに作成した事前座席指定搭乗者情報リストを設定する。 */
    const firstBaggagePassengerInforList = seatPassengerInforList;

    /** 3.3. コールセンター申込手荷物情報に設定する値を作成する。 */
    const callCenterBaggageFlightInforList = this.getCallCenterBaggageFlightInforList(flightsByUN);
    /** 3.3.4. コールセンター申込手荷物搭乗者情報リストに作成した事前座席指定搭乗者情報リストを設定する。 */
    const callCenterBaggagePassengerInforList = seatPassengerInforList;

    /** 3.4. ラウンジ情報に設定する値を作成する。 */
    const loungeFlightInforList = this.getLoungeFlightInforList(flightsByUN, this.pnr?.travelers);
    /** 3.4.4. ラウンジ搭乗者情報リストに作成した事前座席指定搭乗者情報リストを設定する。 */
    const loungePassengerInforList = seatPassengerInforList;

    /** 3.6. ペットらくのり情報に設定する値を作成する。 */
    /** 3.6.3. 当該セグメント情報.statusCode≠”UN”の場合、かつ当該セグメント情報.isStandbyCheckInHL=falseの場合、ペットらくのりセグメント情報リストに当該セグメント情報を追加する。 */
    const petRakunoriFlightInforList = flightsByUN;
    /** 3.6.4. ペットらくのり搭乗者情報リストに作成した事前座席指定搭乗者情報リストを設定する。 */
    const petRakunoriPassengerInforList = seatPassengerInforList;

    // 3.7.	マイカーバレー情報に設定する値を作成する。
    // 3.7.3. PNR情報取得API応答.data.services.myCarvaletが存在するの場合、以下の処理を行う
    let myCarValetFlightInforList: Array<Type1> = [];
    if (isEmptyObject(this.pnr?.services?.myCarValet) !== true) {
      myCarValetFlightInforList = flightsByUN;
    }
    // 3.7.4.	マイカーバレー搭乗者情報リストに作成した事前座席指定搭乗者情報リストを設定する。
    const myCarValetPassengerInforList = seatPassengerInforList;

    // 3.8.3.	当該セグメント情報.statusCode≠”UN”の場合、かつ当該セグメント情報.isStandbyCheckInHL=falseの場合、ジュニアパイロットセグメント情報リストに追加する。
    const juniorPilotFlightInforList = flightsByUNForJuniorPilotFlightInforList;
    // 3.8.4.	ジュニアパイロット搭乗者情報リストに作成した事前座席指定搭乗者情報リストを設定する。
    const juniorPilotPassengerInforList = seatPassengerInforList;

    /** 3.9. アテンド情報に設定する値を作成する。 */
    const attendFlightInforList = this.getAttendFlightInforList(flightsByUN);
    /** 3.9.4. アテンド搭乗者情報リストに作成した事前座席指定搭乗者情報リストを設定する。 */
    const attendPassengerInforList = seatPassengerInforList;

    /** 3.10. 配慮系サポート情報に設定する値を作成する。 */
    // 3.10.1 配慮系サポート搭乗者情報リストを空で作成する。
    // 3.10.2 配慮系サポート搭乗者情報リストに作成した事前座席指定搭乗者情報リストを設定する。
    const supportPassengerInforList = seatPassengerInforList;

    this._myBookingServiceService.mybookingServiceState$.next({
      ...this._myBookingServiceService.mybookingServiceState$.value,
      pnr: this.pnr,
      flights: flights,
      firstBaggage: this.firstBaggage,
      seatReservationStatus: this.seatReservationStatus,
      flightsByUN: flightsByUN,
      travelersByINF: travelersByINF,
      // 事前座席指定セグメント情報リスト
      seatFlightInforList: seatFlightInforList,
      // 事前座席指定搭乗者情報リスト
      seatPassengerInforList: seatPassengerInforList,
      // 事前追加手荷物バウンド情報リスト
      firstBaggageBoundInforList: firstBaggageBoundInforList,
      // 事前追加手荷物搭乗者情報リスト
      firstBaggagePassengerInforList: firstBaggagePassengerInforList,
      // コールセンター申込手荷物セグメント情報リスト
      callCenterBaggageFlightInforList: callCenterBaggageFlightInforList,
      // コールセンター申込手荷物搭乗者情報リスト
      callCenterBaggagePassengerInforList: callCenterBaggagePassengerInforList,
      // ラウンジセグメント情報リスト
      loungeFlightInforList: loungeFlightInforList,
      // ラウンジ搭乗者情報リスト
      loungePassengerInforList: loungePassengerInforList,
      // 機内食セグメント情報リスト
      mealFlightInforList: [],
      // 機内食搭乗者情報リスト
      mealPassengerInforList: [],
      // ペットらくのりセグメント情報リスト
      petRakunoriFlightInforList: petRakunoriFlightInforList,
      // ペットらくのり搭乗者情報リスト
      petRakunoriPassengerInforList: petRakunoriPassengerInforList,
      // マイカーバレーセグメント情報リスト
      myCarValetFlightInforList: myCarValetFlightInforList,
      // マイカーバレー搭乗者情報リスト
      myCarValetPassengerInforList: myCarValetPassengerInforList,
      // ジュニアパイロットセグメント情報リスト
      juniorPilotFlightInforList: juniorPilotFlightInforList,
      // ジュニアパイロット搭乗者情報リスト
      juniorPilotPassengerInforList: juniorPilotPassengerInforList,
      // アテンドセグメント情報リスト
      attendFlightInforList: attendFlightInforList,
      // アテンド搭乗者情報リスト
      attendPassengerInforList: attendPassengerInforList,
      // 配慮系サポート搭乗者情報リスト
      supportPassengerInforList: supportPassengerInforList,
    });
  };

  getFirstBaggageBoundInforList = () => {
    let bounds: Bound[] = [];
    /** 3.2.2. 事前追加手荷物搭乗者情報リストを空で作成する。*/
    /** ＜以下、PNR情報取得API応答.data.air.boundsの要素数分繰り返し＞(それぞれの要素が当該バウンド情報) */
    this.pnr?.air?.bounds
    // "当該バウンド情報.flights[]の内にisStandbyCheckInHL=trueである要素が存在する場合、次の当該バウンド情報の処理に進む。"
      ?.filter((bound) => !bound?.flights?.some((flight) => flight?.isStandbyCheckInHL === true))
      ?.forEach((bound) => {
      /**
       * 3.2.4.	PNR情報取得API応答.data.services.baggage.firstBaggage.<当該バウンド情報.airBoundId>.isAvailable=true(申込可)、
       * またはPNR情報取得API応答.data.services.baggage.firstBaggage.<当該バウンド情報.airBoundId>.<当該搭乗者情報.id>serviceStatus≠
       * ""(空欄）の場合、
       * バウンドフラグ = trueを設定する。
       */
      const firstBaggage =
        ((this.pnr?.services?.baggage?.firstBaggage as FirstBaggagePnr)?.[
          bound?.airBoundId!
        ] as FirstBaggageSegmentMap) ?? ({} as FirstBaggageSegmentMap);

      const initBoundFlag = this.travelers?.some((traveler) => {
        const firstBaggagePassengerMap = firstBaggage?.[String(traveler.id)] as FirstBaggagePassengerMap;
        return (
          Boolean(firstBaggage?.isAvailable) === true || (firstBaggagePassengerMap?.serviceStatus as string) !== ''
        );
      });

      if (initBoundFlag) {
        bounds.push(bound);
      }
    });
    /** ＜ここまで、PNR情報取得API応答.data.air.boundsの要素数分繰り返し＞ */
    return bounds;
  };

  getCallCenterBaggageFlightInforList = (flightsByUN?: Array<Type1>): Array<Type1> => {
    const flights: Type1[] = [];
    const flightsByUNs = flightsByUN ?? [];
    for (const flight of flightsByUNs) {
      const callCenterRequestBaggage = (
        this.pnr?.services?.baggage?.callCenterRequestBaggage as CallCenterRequestBaggage
      )?.[flight?.id!];
      /** 3.3.3. 当該セグメント情報.statusCode≠”UN” かつ PNR情報取得API応答.data.services.baggage.callCenterRequestBaggage.<当該セグメント情報.セグメントID>が存在する場合、コールセンター申込手荷物セグメント情報リストに当該セグメント情報を追加する。 */
      if (callCenterRequestBaggage) {
        flights.push(flight);
      }
    }
    return flights;
  };

  getLoungeFlightInforList = (flightsByUN?: Array<Type1>, travelers?: Traveler[]): Array<Type1> => {
    let flights: Type1[] = [];
    /** 3.4.3.	当該セグメント情報.statusCode≠”UN” の場合		 */
    const flightsByUNs = flightsByUN ?? [];
    for (const flight of flightsByUNs) {
      const lounge = (this.pnr?.services?.lounge as Lounge)?.[flight?.id!];
      const isConditonExist = travelers?.some((traveler: Traveler) => {
        const travelerId = traveler.id ?? '';
        return (
          isStringEmpty((lounge?.[travelerId] as PassengerLoungeMap)?.code) !== true ||
          (lounge?.[travelerId] as PassengerLoungeMap)?.isMyLoungeRequested
        );
      });
      /** 3.4.3.1.1.	PNR情報取得API応答.data.services.lounge.<当該セグメント情報.セグメントID>.isAvailable=true(申込可) */
      /** 3.4.3.1.2.	当該セグメント情報.flightServices.isLoungeAvailable=true(ラウンジ利用可否)
      /** 3.4.3.1.3. PNR情報取得API応答.data.services.lounge.<当該セグメント情報.セグメントID>.<当該搭乗者情報.搭乗者ID>.code≠""(空欄)が存在する
       ** 3.4.3.1.4. PNR情報取得API応答.data.services.lounge.<当該セグメント情報.セグメントID>.<当該搭乗者情報.搭乗者ID>.isMyLoungeRequested=true
		＜以下、PNR情報取得API応答.data.travelersの要素数分繰り返し＞(それぞれの要素が当該搭乗者情報)	*/
      if (lounge?.isAvailable === true || flight?.flightServices?.isLoungeAvailable === true || isConditonExist) {
        flights.push(flight);
      }
    }
    return flights;
  };
  /** 3.5.3. 当該セグメント情報.statusCode≠”UN” の場合、以下のいずれかに該当する場合、機内食セグメント情報リストに当該セグメント情報を追加する。 */
  getMealFlightInforList = (flightsByUN?: Array<Type1>, travelers?: Traveler[]): Array<Type1> => {
    let flights: Type1[] = [];
    const flightsByUNs = flightsByUN ?? [];
    for (const flight of flightsByUNs) {
      const meal = (this.pnr?.services?.meal as Meal)?.[flight?.id!] as FlightMealInfo;
      const hasAppliedMealCode = travelers?.some((traveler: Traveler) => {
        const travelerId = traveler.id ?? '';
        return (meal?.[travelerId] as TravelerMealInfo)?.appliedMealList?.some(
          (appliedMeal: AppliedMealList) => isStringEmpty(appliedMeal.code) !== true
        );
      });

      if (
        /** 3.5.3.1 PNR情報取得API応答.data.services.meal.<当該セグメント情報.セグメントID>.isAvailable=true(申込可)場合、*/
        meal?.isAvailable === true ||
        /** 3.5.3.2 PNR情報取得API応答.data.services.meal.<当該セグメント情報.セグメントID>.<当該搭乗者情報.搭乗者ID>.appliedMealListの中に、code≠""(空欄)が存在する */
        hasAppliedMealCode ||
        /**
         * 3.5.3.3.	当該セグメント情報.isJapanDomesticFlight=true、かつ、当該セグメント情報.meal.mealCodeが存在する
         */
        (flight?.isJapanDomesticFlight === true && !isStringEmpty(flight?.meal?.mealCode))
      ) {
        flights.push(flight);
      }
    }
    return flights;
  };

  getAttendFlightInforList = (flightsByUN?: Array<Type1>): Array<Type1> => {
    const flights: Type1[] = [];
    const flightsByUNs = flightsByUN ?? [];
    for (const flight of flightsByUNs) {
      const airportService = (this.pnr?.services?.airportService as AirportService)?.[flight?.id!];
      /** 3.9.3. 当該セグメント情報.statusCode≠”UN”　かつ PNR情報取得API応答.data.services.airportService.<当該セグメント情報.セグメントID>が存在する場合、*/
      if (airportService) {
        flights.push(flight);
      }
    }
    return flights;
  };

  /** 画面終了時処理 */
  destroy(): void {
    this._servicingCommonStoreService.updateServicingCommon({
      previousPageIdentifier: PREVIOUS_PAGE_IDENTIFIER.MYBOOKING_SERVICE,
    });
    this._subscriptions.unsubscribe();
  }

  /** 画面更新時処理 */
  reload(): void {}
}
